import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import merge from 'lodash.merge';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { FulfillmentPlannerContextProvider } from '../context';

import { getInitialReturnPackageShippingConfiguration } from '../helpers/get-return-package-object';
import { getPrimaryOrFirstSellerLabelProvider } from '../helpers/get-return-service';
import { getShipFromIdOrDefault } from '../helpers/get-ship-from';
import { emptyObject } from 'ui-components/default-props/empty';
import Global from '../../store/global-selectors';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { DimensionUnit } from '../../enums/dimension-unit';
import { WeightUnit } from '../../enums/weight-unit';
import { RETURNS } from '../../enums/csw-types';
import * as Actions from '../../store/csw/csw.actions';
import { getReturnShipmentConfiguration } from '../../store/returns/returns.api';
import { useReturnService, useReturnServiceCollectionGuid } from '../../hooks/use-seller-setting';
import { useReturnConfig } from '../../orders/order-details/enhanced-return/return-config-provider';
import { LogService } from '../../services/log-service';
import { ShipmentLabelType } from '../../enums/shipment-label-type';

const DIMENSION_UNIT_MAP = {
  in: DimensionUnit.Inches,
  cm: DimensionUnit.Centimeters,
};

const WEIGHT_UNIT_MAP = {
  oz: WeightUnit.Ounces,
  g: WeightUnit.Grams,
  kg: WeightUnit.Kilograms,
  lb: WeightUnit.Pounds,
};

const normalizeDimensions = (dimensions) => {
  if (!dimensions) return dimensions;
  return {
    ...dimensions,
    unit: DIMENSION_UNIT_MAP[dimensions.unit] ?? dimensions.unit,
  };
};

const normalizeWeight = (weight) => {
  if (!weight) return weight;
  return {
    ...weight,
    unit: WEIGHT_UNIT_MAP[weight.unit] ?? weight.unit,
  };
};

const mergePackageDetails = (basePackages, { weight, dimensions, packageTypeId }) => {
  const [firstPackage = {}, ...rest] = basePackages;
  return [
    {
      ...firstPackage,
      ...(weight && { weight }),
      ...(dimensions && { dimensions }),
      ...(packageTypeId && { packageTypeId }),
    },
    ...rest,
  ];
};

/**
 * @type {React.FC<{
 *   children: React.ReactNode;
 *   fulfillmentPlanId: string;
 *   isAwaitingShipment?: boolean;
 *   returnId?: string;
 *   salesOrderId: string;
 * }>}
 */
export const EnhancedReturnAdapterForFulfillmentPlannerContext = React.memo(
  ({
    children,
    fulfillmentPlanId, // looks like: 'return-<salesOrderId>'
    isAwaitingShipment = false,
    returnId,
    salesOrderId,
  }) => {
    // getState is a stable reference that allows us to read from the store only once, which is ideal
    // since we only use most of these values to initialize state in the FulfillmentPlannerContext
    const { getState } = useStore();

    // TODO: SPD-14722 — the shipTo should probably come from the outgoing shipment (like the shipFrom does above).
    // Also try to investigate whether or not this is only an initialization value or if it's subscribed to by a
    // consumer of the FulfillmentPlannerContext somewhere.
    const shipTo = useSelector((state) => {
      return (
        Global.selectEntitiesSalesOrderProperty(state, {
          salesOrderId,
          property: 'shipTos.0',
        }) || emptyObject
      );
    });

    const storeGuid = useSelector((state) =>
      Global.selectEntitiesSalesOrderProperty(state, {
        salesOrderId,
        property: 'store.storeGuid',
      }),
    );

    const savedReturnServiceId = useReturnService();
    const savedServiceCollectionGuid = useReturnServiceCollectionGuid();

    const initialPackages = useMemo(() => getInitialReturnPackageShippingConfiguration(getState()), [getState]);
    const [packages, setPackages] = useState(initialPackages);
    const [shipFromId, setShipFromId] = useState(null);
    const [insuranceProvider, setInsuranceProvider] = useState(InsuranceProviders.None);
    const [otherShippingOptions, setOtherShippingOptions] = useState({
      confirmationEnumId: 0,
    });
    const initialServiceIdAndSellerLabelProviderId = useMemo(() => {
      if (!savedReturnServiceId) {
        return { serviceId: null, sellerLabelProviderId: null };
      }
      const state = getState();
      const matchingService = Global.selectSellerServices(state).find((service) => service.serviceId === savedReturnServiceId) || {};
      const sellerLabelProviders = Global.selectSellerLabelProviders(state);
      const labelProvider = getPrimaryOrFirstSellerLabelProvider(matchingService.labelProviderId, sellerLabelProviders);
      return {
        serviceId: savedReturnServiceId,
        sellerLabelProviderId: labelProvider.sellerLabelProviderId ?? null,
      };
    }, [getState, savedReturnServiceId]);
    const [serviceIdAndSellerLabelProviderId, setServiceIdAndSellerLabelProviderId] = useState(initialServiceIdAndSellerLabelProviderId);
    const initialServiceCollectionGuid = useMemo(() => {
      if (savedServiceCollectionGuid) {
        return savedServiceCollectionGuid;
      }
      if (savedReturnServiceId) {
        return null;
      }
      const serviceCollections = Global.selectSellerServiceCollections(getState());
      const firstActiveCollection = serviceCollections.find((s) => s.isActive);
      return firstActiveCollection?.serviceCollectionGuid ?? null;
    }, [getState, savedReturnServiceId, savedServiceCollectionGuid]);
    const [serviceCollectionGuid, setServiceCollectionGuid] = useState(initialServiceCollectionGuid);
    const [availableSellerShippingServices, setAvailableSellerShippingServices] = useState([]);

    const dispatch = useDispatch();

    const [bestWayToShip, setBestWayToShip] = useAsyncSafeState({});

    const { labelType } = useReturnConfig();

    const displaySchemeForPayload = useMemo(() => {
      if (ShipmentLabelType[labelType]) return labelType;
      LogService.logInfo('Unknown labelType omitted from return getRate payload', { labelType });
      return null;
    }, [labelType]);

    useEffect(() => {
      dispatch(Actions.syncContextToReducer(fulfillmentPlanId, { displayScheme: displaySchemeForPayload ?? undefined }));
    }, [dispatch, fulfillmentPlanId, displaySchemeForPayload]);

    const dispatchRateRequest = useCallback(
      ({ packages: ratePackages, shipFromId: rateShipFromId, serviceId, sellerLabelProviderId, serviceCollectionGuid: rateScGuid }) => {
        dispatch(
          Actions.getRateWithPayload(
            fulfillmentPlanId,
            {
              shipTo,
              email: '',
              availableSellerShippingServices: [],
              isReturn: true,
              packages: ratePackages,
              shipFromId: rateShipFromId,
              shipDate: null,
              insuranceProvider: InsuranceProviders.None.name,
              options: { confirmationEnumId: 0 },
              serviceId,
              sellerLabelProviderId,
              serviceCollectionGuid: rateScGuid,
              driverTip: null,
              pickup: null,
              customs: null,
              needsCustoms: false,
              setBestWayToShip,
              ...(displaySchemeForPayload ? { displayScheme: displaySchemeForPayload } : {}),
            },
            RETURNS,
          ),
        );
      },
      [dispatch, fulfillmentPlanId, shipTo, setBestWayToShip, displaySchemeForPayload],
    );

    useEffect(() => {
      if (!returnId || !storeGuid || !isAwaitingShipment) {
        dispatch(
          Actions.getBestWayToShipOnMount(
            fulfillmentPlanId,
            {
              shipTo,
              email: '',
              availableSellerShippingServices: [],
              isReturn: true,
              packages: initialPackages,
              shipFromId: null,
              shipDate: null,
              insuranceProvider: InsuranceProviders.None.name,
              options: { confirmationEnumId: 0 },
              serviceId: null,
              sellerLabelProviderId: null,
              serviceCollectionGuid: null,
              driverTip: null,
              pickup: null,
              customs: null,
              needsCustoms: false,
              setBestWayToShip,
              ...(displaySchemeForPayload ? { displayScheme: displaySchemeForPayload } : {}),
            },
            RETURNS,
          ),
        );
        return;
      }
      getReturnShipmentConfiguration(storeGuid, returnId)
        .then((shipmentDetails) => {
          const weight = normalizeWeight(shipmentDetails?.weight);
          const dimensions = normalizeDimensions(shipmentDetails?.dimensions);
          const returnShipFromId = shipmentDetails?.shipFromId ?? null;
          const packageTypeId = shipmentDetails?.packageTypeId;

          const resolvedPackages = mergePackageDetails(initialPackages, { weight, dimensions, packageTypeId });
          if (weight || dimensions || packageTypeId) {
            setPackages(resolvedPackages);
          }
          setShipFromId(returnShipFromId);

          let resolvedServiceId = null;
          let resolvedSellerLabelProviderId = null;
          if (shipmentDetails?.serviceId) {
            const state = getState();
            const matchingService =
              Global.selectSellerServices(state).find((service) => service.serviceId === shipmentDetails.serviceId) || {};
            const { labelProviderId } = matchingService;
            const sellerLabelProviders = Global.selectSellerLabelProviders(state);
            const labelProvider = getPrimaryOrFirstSellerLabelProvider(labelProviderId, sellerLabelProviders);
            resolvedServiceId = shipmentDetails.serviceId;
            resolvedSellerLabelProviderId = labelProvider.sellerLabelProviderId ?? null;
            setServiceIdAndSellerLabelProviderId({
              serviceId: resolvedServiceId,
              sellerLabelProviderId: resolvedSellerLabelProviderId,
            });
          }

          let resolvedServiceCollectionGuid = null;
          if (shipmentDetails?.serviceCollectionGuid) {
            resolvedServiceCollectionGuid = shipmentDetails.serviceCollectionGuid;
          } else if (!shipmentDetails?.serviceId) {
            const serviceCollections = Global.selectSellerServiceCollections(getState());
            const firstActiveCollection = serviceCollections.find((s) => s.isActive);
            resolvedServiceCollectionGuid = firstActiveCollection?.serviceCollectionGuid ?? null;
          }
          setServiceCollectionGuid(resolvedServiceCollectionGuid);

          dispatch(
            Actions.syncContextToReducer(fulfillmentPlanId, {
              packages: resolvedPackages,
              shipFromId: returnShipFromId,
              serviceId: resolvedServiceId,
              sellerLabelProviderId: resolvedSellerLabelProviderId,
              serviceCollectionGuid: resolvedServiceCollectionGuid,
            }),
          );

          dispatchRateRequest({
            packages: resolvedPackages,
            shipFromId: returnShipFromId,
            serviceId: resolvedServiceId,
            sellerLabelProviderId: resolvedSellerLabelProviderId,
            serviceCollectionGuid: resolvedServiceCollectionGuid,
          });
        })
        .catch(() => {
          dispatchRateRequest({
            packages: initialPackages,
            shipFromId: null,
            serviceId: initialServiceIdAndSellerLabelProviderId.serviceId,
            sellerLabelProviderId: initialServiceIdAndSellerLabelProviderId.sellerLabelProviderId,
            serviceCollectionGuid: initialServiceCollectionGuid,
          });
        });
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [returnId, storeGuid, isAwaitingShipment]);

    const onConfigurationChange = useCallback(
      (payload) => {
        if (payload?.packages) {
          setPackages(payload.packages);
        }
        // check !== undefined for any value which may be "unset" (null) by the user or by the system to allow the null value
        if (payload?.shipFromId !== undefined) {
          setShipFromId(payload.shipFromId);
        }
        if (payload?.insuranceProvider) {
          setInsuranceProvider(InsuranceProviders[payload.insuranceProvider]);
        }
        if (payload?.options) {
          setOtherShippingOptions(payload.options);
        }
        if (
          payload?.serviceId !== undefined ||
          payload?.sellerLabelProviderId !== undefined ||
          payload?.serviceCollectionGuid !== undefined
        ) {
          setServiceIdAndSellerLabelProviderId({
            serviceId: payload.serviceId,
            sellerLabelProviderId: payload.sellerLabelProviderId,
          });
          setServiceCollectionGuid(payload.serviceCollectionGuid);
        }

        const completePayload = merge(
          {
            shipTo,
            email: '',
            availableSellerShippingServices,
            isReturn: true,
            packages,
            shipFromId,
            shipDate: null,
            insuranceProvider: insuranceProvider.name,
            options: otherShippingOptions,
            serviceId: serviceIdAndSellerLabelProviderId.serviceId,
            sellerLabelProviderId: serviceIdAndSellerLabelProviderId.sellerLabelProviderId,
            serviceCollectionGuid,
            driverTip: null,
            pickup: null,
            customs: null,
            needsCustoms: false,
            setBestWayToShip,
            ...(displaySchemeForPayload ? { displayScheme: displaySchemeForPayload } : {}),
          },
          payload,
        );
        dispatch(Actions.getRateWithPayload(fulfillmentPlanId, completePayload, RETURNS));
      },
      [
        dispatch,
        fulfillmentPlanId,
        availableSellerShippingServices,
        shipTo,
        packages,
        shipFromId,
        insuranceProvider,
        otherShippingOptions,
        serviceIdAndSellerLabelProviderId,
        serviceCollectionGuid,
        setBestWayToShip,
        displaySchemeForPayload,
      ],
    );

    const triggerRate = useCallback(() => {
      onConfigurationChange({});
    }, [onConfigurationChange]);

    const triggerRateRef = useRef(triggerRate);
    triggerRateRef.current = triggerRate;
    const hasTriggeredInitialRateRef = useRef(false);
    useEffect(() => {
      if (!hasTriggeredInitialRateRef.current) {
        hasTriggeredInitialRateRef.current = true;
        return;
      }
      triggerRateRef.current();
    }, [labelType]);

    const sellerShipFroms = useSelector(Global.selectSellerShipFroms);
    const resolvedShipFromId = getShipFromIdOrDefault(shipFromId, sellerShipFroms);

    return (
      <FulfillmentPlannerContextProvider
        key={fulfillmentPlanId}
        availableSellerShippingServices={availableSellerShippingServices}
        bestWayToShip={bestWayToShip}
        cswTypeId={RETURNS}
        fulfillmentPlanId={fulfillmentPlanId}
        insuranceProvider={insuranceProvider}
        isReturn
        onAvailableSellerShippingServicesChange={setAvailableSellerShippingServices}
        onConfigurationChange={onConfigurationChange}
        otherShippingOptions={otherShippingOptions}
        packages={packages}
        serviceCollectionGuid={serviceCollectionGuid}
        serviceIdAndSellerLabelProviderId={serviceIdAndSellerLabelProviderId}
        shipFromId={resolvedShipFromId}
        triggerRate={triggerRate}
      >
        {children}
      </FulfillmentPlannerContextProvider>
    );
  },
);
